import { useEffect } from "react";
import {
  updateActionDescription,
  updateTriggerDescription,
} from "../services/updateDescription";
import type { Integrations } from "../utils/constants";
import type { ActionCandidate } from "../types/candidate";
import { useRootStore } from "../stores/hooks";
import { useEngineClient } from "../services/engineClient";

/**
 * Updates JSON editor descriptions for trigger and actions when activeAction changes.
 * This hook fetches the latest output descriptions from the engine for webhook-related
 * triggers and actions to ensure the JSON editor has up-to-date output data available.
 * The descriptions are updated for all webhook actions except the currently active one.
 */
export const useUpdateJSONEditorDescription = (
  integrationIds: Integrations[],
  activeAction?: string,
): void => {
  const store = useRootStore();
  const engineClient = useEngineClient();

  const candidate = store.automations.candidate;
  const { trigger } = candidate.self;
  const actions = candidate.allActions;

  useEffect(() => {
    if (activeAction === undefined) {
      return;
    }

    const triggerPromise =
      integrationIds.includes(
        trigger.triggerReference?.integrationId as Integrations,
      ) &&
      updateTriggerDescription(
        candidate,
        false,
        engineClient,
        store.automations,
        store.integrations,
      );

    const actionsPromises = actions
      .filter(
        (a) =>
          a.hash !== activeAction &&
          integrationIds.includes(
            a.actionReference?.integrationId as Integrations,
          ),
      )
      .map(async (action: ActionCandidate) => {
        return await updateActionDescription(
          candidate,
          action,
          store.integrations.list,
          engineClient,
          store.automations,
        );
      });
    void Promise.all([triggerPromise, ...actionsPromises]);
  }, [activeAction]);
};
